import dayjs from 'dayjs';
import {CollectionTotal} from 'model/collection/CollectionModels'

export function getRandom(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
}

export function getRandomElement(arr: string[]): string {
  const idx = getRandom(0, arr.length);
  return arr[idx];
}

export function isNumber(value: string): boolean {
    return Number.isNaN(Number(value)) === false;
}

export const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT;
export const REACT_APP_VERIFY_EMAIL = process.env.REACT_APP_VERIFY_EMAIL;
export const REACT_APP_CAPTCHA = process.env.REACT_APP_CAPTCHA;

export const COMMON_HEADERS = {
  'Content-Type': 'application/json', 
  'Accept': 'application/json'
};

export const log = (msg: any): void => {
  if (REACT_APP_VERIFY_EMAIL === 'false') {
    console.log(msg);
  }
}

export const arrayRange = (start: number, stop: number, step: number) =>
  Array.from(
  { length: (stop - start) / step + 1 },
  (value, index) => start + index * step
);

export const replaceHandlebars = (replacements: Map<string, any>, inputObj: string) => {
  let res = inputObj
  for (let [key, value] of  replacements.entries()) {
    res = res.replaceAll(key, value)
  }  

  return res;
}

export const capitalizeFirstLetter = (str: string) => {
  
  return str
    .toString()
    .split('_')
    .map(word => {
        if (word.length === 0) return '';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

export const insertAtCaret = (textField: any, textToInsert: string) => {
  const startPos = textField.selectionStart;
  const endPos = textField.selectionEnd;
  const currentValue = textField.value;
  
  const newValue = 
      currentValue.substring(0, startPos) + 
      textToInsert + 
      currentValue.substring(endPos);
  
  // Calculate new cursor position
  const newCursorPos = startPos + textToInsert.length;
  
  // Update the value
  textField.value = newValue;
  
  // Restore cursor position
  textField.selectionStart = newCursorPos;
  textField.selectionEnd = newCursorPos;
  
  return newValue;
}

export const splitIntoSentences = (text: string, delimiters: string[]) => {
  // Handle edge cases
  const result = [];
  let currentPosition = 0;
  
  while (currentPosition < text.length) {
      // Find the next occurrence of each delimiter
      let nextSplit = {
          position: text.length,
          delimiter: ""
      };
      
      for (const delimiter of delimiters) {
          const position = text.indexOf(delimiter, currentPosition);
          if (position !== -1 && position < nextSplit.position) {
              nextSplit = {
                  position: position,
                  delimiter: delimiter
              };
          }
      }
      
      // Extract the sentence and add to results
      const sentence = text.slice(currentPosition, nextSplit.position);
      if (sentence) {
          result.push({
              text: sentence,
              delimiter: nextSplit.delimiter
          });
      }
      
      // Move position past the delimiter
      currentPosition = nextSplit.position + (nextSplit.delimiter?.length || 0);
  }
  
  return result;
}

export const getDaysString = (diffDays: number) => {
  if (diffDays < 1) {
      return "Today";
  }
  if (diffDays < 2) {
      return "Tomorrow";
  } 

  return "in " + diffDays + " days";
}

export const getStatusString = (
  endDate: string, 
  schedule: string, 
  dayOfWeek: number, 
  dayOfMonth: number): string => {
  let end = dayjs(endDate); // String deep copy
  if (end.isBefore(dayjs())) {
    return `Expired on ${end.format("YYYY-MM-DD")}`
  }

  return getDaysString(getUpcoming(schedule, dayOfWeek, dayOfMonth));
}

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase()
  + str.slice(1)
}

export const getUpcoming = (schedule: string, dayOfWeek: number, dayOfMonth: number, utcOffset?: number) => {
  const now = new Date(); // Current date and time
  const today = now.getDate(); // Day of the month (1-31)
  const currentDayOfWeek = now.getDay(); // Day of the week (0-6, where 0 = Sunday)

  if (schedule === "monthly") {
      const lastDay = getDaysInMonth(now); // Last day of the current month
      const adjusted = getLastDayOfMonth(dayOfMonth);
      return (adjusted - today + lastDay) % lastDay;
  }

  if (schedule === "weekly") {
      // Day of the week in JavaScript: 0 (Sunday) to 6 (Saturday)
      // Adjust to match Java convention: 1 (Monday) to 7 (Sunday)
      const jsDayOfWeek = (currentDayOfWeek === 0) ? 7 : currentDayOfWeek;
      return (dayOfWeek - jsDayOfWeek + 7) % 7;
  }

  throw new Error(`Invalid schedule: ${schedule}`);
}

export const getLastDayOfMonth = (setting: number) => {
  const now = new Date();
  const lastDay = getDaysInMonth(now); // Last day of the current month
  return Math.min(setting, lastDay); // Return the smaller of the two values
}

export const getDaysInMonth = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based (0 = January, 11 = December)
  return new Date(year, month + 1, 0).getDate(); // Last day of the month
}

export const calculateAmountSummary = (collectionTotals: CollectionTotal[]) => {
  let totalAmount = 0;

  // Loop through the object
  for (let collectionTotal of collectionTotals) {
    totalAmount += collectionTotal.total;
  }

  return totalAmount;
}

export const servicePercentage = (collectionTotal: CollectionTotal, total: number) => {

  return (collectionTotal.total*100)/total;
}


export const findClosestNumber = (list: number[], x: number): number | null => {
  if (list.length === 0) return null;
    
  return list.filter(num => num >= x).reduce((closest, num) => 
      Math.abs(num - x) < Math.abs(closest - x) ? num : closest, list[list.length-1]
  ) || null;
}


export const base64Encode = (str: string): string => {
  // For browser environments
  if (typeof window !== 'undefined' && window.btoa) {
    // Use TextEncoder for proper UTF-8 handling
    const bytes = new TextEncoder().encode(str);
    const binString = Array.from(bytes).map(byte => String.fromCharCode(byte)).join('');
    return window.btoa(binString);
  }
  
  // For Node.js environments
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(str).toString('base64');
  }
  
  throw new Error('No base64 encoding method available');
}

export const base64Decode = (base64Str: string): string => {
  // For browser environments
  if (typeof window !== 'undefined' && window.atob) {
    const binString = window.atob(base64Str);
    const bytes = Uint8Array.from(binString, char => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  
  // For Node.js environments
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64Str, 'base64').toString();
  }
  
  throw new Error('No base64 decoding method available');
}

export const getNextEmailDate = (schedule: string, dayOfTheMonth: number | null, dayOfTheWeek: number | null): dayjs.Dayjs => {
  const now = dayjs();
  const sentTodayAlready = now.hour() >= 11;

  if (schedule === "monthly") {
    const targetDay = dayOfTheMonth || 1;
    let nextDate = now.date(targetDay).startOf('day');
    if (nextDate.isBefore(now, 'day') || (nextDate.isSame(now, 'day') && sentTodayAlready)) {
      nextDate = nextDate.add(1, 'month');
    }
    if (nextDate.date() !== targetDay) {
      nextDate = nextDate.endOf('month').startOf('day');
    }
    return nextDate;
  } else {
    const targetDayOfWeek = dayOfTheWeek || 1;
    let nextDate = now.day(targetDayOfWeek).startOf('day');
    if (nextDate.isBefore(now, 'day') || (nextDate.isSame(now, 'day') && sentTodayAlready)) {
      nextDate = nextDate.add(1, 'week');
    }
    return nextDate;
  }
};

export const getScheduleMessage = (schedule: string, dayOfTheMonth: number | null, dayOfTheWeek: number | null): string => {
  const nextDate = getNextEmailDate(schedule, dayOfTheMonth, dayOfTheWeek);
  let message = `The next email will be sent on ${nextDate.format("MMM Do, YYYY")}.`;
  
  if (schedule === "monthly" && dayOfTheMonth && dayOfTheMonth > 28) {
    message += ` Some months do not have day ${dayOfTheMonth}. For these months, the email will be sent on the last day of the month.`;
  }
  
  return message;
};
